/**
 * Client-facing guides.
 *
 * These are evergreen references handed to clients during a project — the kind
 * of thing that gets sent once per client and read once. Unlike blog posts they
 * are held in the repo as markdown rather than in Supabase, so they are version
 * controlled, reviewable in a diff, and keep working when the database doesn't.
 *
 * Adding one: drop a .md file in src/content/guides/, import it below, and add
 * an entry to `guides`. The routes pick it up automatically.
 */
import { marked } from 'marked';
import '../styles/prose.css';
import googleBusinessProfileApi from '../content/guides/google-business-profile-api.md?raw';
import addDeveloperToCloudProject from '../content/guides/add-developer-to-cloud-project.md?raw';
import linkBillingToCloudProject from '../content/guides/link-billing-to-cloud-project.md?raw';
import cloudflareRedirectWwwAndHttps from '../content/guides/cloudflare-redirect-www-and-https.md?raw';

export const guides = [
  {
    slug: 'google-business-profile-api',
    title: 'How to Get Google Business Profile API Access (And What It Unlocks)',
    excerpt:
      'Without API access your website can only ever show five Google reviews — a hard cap you cannot pay to raise. What approval unlocks, the four prerequisites Google checks, and how to apply without getting rejected.',
    category: 'Local SEO',
    readTime: '7 min read',
    updated: '2026-09-04',
    body: googleBusinessProfileApi,
  },
  {
    slug: 'add-developer-to-cloud-project',
    title: 'How to Add Your Developer to Your Google Cloud Project',
    excerpt:
      'Your developer needs to enable APIs and set up OAuth in your project — and none of that needs your password. How to grant access as an Editor, why not Owner, and how to remove it when the work is done.',
    category: 'Access',
    readTime: '3 min read',
    updated: '2026-09-16',
    body: addDeveloperToCloudProject,
  },
  {
    slug: 'link-billing-to-cloud-project',
    title: 'How to Link a Billing Account to Your Google Cloud Project',
    excerpt:
      'Adding a card to Google Cloud and linking it to your project are two separate steps, and APIs stay locked until you do both. How to link it, why your developer can\'t do it for you, and what to check when the card won\'t show up.',
    category: 'Access',
    readTime: '4 min read',
    updated: '2026-09-21',
    body: linkBillingToCloudProject,
  },
  {
    slug: 'cloudflare-redirect-www-and-https',
    title: 'How to Redirect www and http to Your Main Web Address in Cloudflare',
    excerpt:
      'Your website can be reached at four slightly different addresses, and Google can treat them as four copies. How to forward them all to one with two Cloudflare settings, no code, and screenshots of every click.',
    category: 'Domains',
    readTime: '5 min read',
    updated: '2026-10-13',
    body: cloudflareRedirectWwwAndHttps,
  },
];

/**
 * marked emits a bare <table>. Wrapping it lets a wide table scroll inside its
 * own box instead of pushing the whole page sideways on a phone.
 */
const wrapTables = (html) =>
  html.replace(/<table>/g, '<div class="rm-table-scroll"><table>').replace(/<\/table>/g, '</table></div>');

// Parsed as local time. A bare "2026-09-16" is UTC midnight, which renders as
// the day before anywhere west of Greenwich.
const formatDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

export async function renderGuidesPage(container) {
  const cards = guides
    .map(
      (guide) => `
      <article class="rm-blog-card">
        <a href="/guides/${guide.slug}" class="rm-blog-link">
          <div class="rm-blog-meta">
            <span class="rm-blog-date">Updated ${formatDate(guide.updated)}</span>
            <span class="rm-blog-readtime">${guide.readTime}</span>
          </div>
          <h2 class="rm-blog-title">${guide.title}</h2>
          <p class="rm-blog-excerpt">${guide.excerpt}</p>
          <div class="rm-blog-footer">
            <span class="rm-blog-category">${guide.category}</span>
            <span class="rm-read-more">Read the guide →</span>
          </div>
        </a>
      </article>
    `
    )
    .join('');

  container.innerHTML = `
    <section class="rm-page">
      <div class="rm-container">
        <div class="rm-page-header">
          <p class="rm-eyebrow">Guides</p>
          <h1 class="rm-page-title">Practical guides for clients</h1>
          <p class="rm-page-description">
            Walkthroughs for the parts of a project that need something only you can do —
            an account to create, a form to submit, an approval to request.
          </p>
        </div>

        <div class="rm-blog-grid">
          ${cards}
        </div>
      </div>
    </section>
  `;
}

export async function renderGuidePage(container, slug) {
  const guide = guides.find((g) => g.slug === slug);

  if (!guide) {
    container.innerHTML = `
      <section class="rm-page">
        <div class="rm-container">
          <a href="/guides" class="rm-back-link">← All guides</a>
          <h1 class="rm-page-title">Guide not found</h1>
          <p class="rm-page-description">There's no guide at that address.</p>
          <a href="/guides" class="rm-btn rm-btn-primary">See all guides</a>
        </div>
      </section>
    `;
    return;
  }

  document.title = `${guide.title} — Caden Burleson`;

  container.innerHTML = `
    <article class="rm-page">
      <div class="rm-container">
        <a href="/guides" class="rm-back-link">← All guides</a>

        <div class="rm-detail-header">
          <div class="rm-detail-meta">
            <span class="rm-detail-category">${guide.category}</span>
            <span class="rm-detail-date">Updated ${formatDate(guide.updated)}</span>
            <span class="rm-detail-readtime">${guide.readTime}</span>
          </div>
          <h1 class="rm-detail-title">${guide.title}</h1>
        </div>

        <div class="rm-detail-content rm-blog-content">
          ${wrapTables(marked.parse(guide.body))}
        </div>
      </div>
    </article>
  `;
}
